<template>
  <div v-if="problem && problem.id" class="problem-causes-section">
    <div class="v-centerer problem-sheet__section-title-wrapper">
      <h2 class="basic-sub-title problem-sheet__section-title">
        {{ $t('message.CAUSES') }}
      </h2>

      <ProblemTrust :trust="causesTrust * 100" @click="App.showIA" />
    </div>

    <div>
      <div class="problem-causes-section__causes">
        <div>
          <CauseItem
            v-for="cause in causesInSheet"
            ref="causeItems"
            :key="cause.id"
            :value="cause"
            :selected="cause.id === selectedCauseId"
            :disabled="disabled"
            @causeClick="causeClicked(cause)"
            @pictureClick="editFiles(cause)"
            @textInput="causeEdited(cause, 'text')"
            @delete="deleteCause(cause)"
          />
        </div>
        <div v-if="!disabled">
          <button
            class="generic-border generic-border--text-btn generic-border--blue-border f-w"
            @click="addCause()"
          >
            <div class="generic-border__left">
              <Icon class="generic-border__left-blue-icon">{{ icons.addExtraBold }}</Icon>
              <span>{{ $t('message.ADD_A_CAUSE') }}</span>
            </div>
            <Icon class="generic-border__right-arrow">{{ icons.arrowDownLine }}</Icon>
          </button>
        </div>
      </div>
      <h1 class="legend legend--bottom-margin">
        {{ $t('message.SEEK_ROOT_CAUSES') }}
      </h1>
      <div class="f-w">
        <RouterLink
          class="generic-border generic-border--text-btn generic-border--blue-border mt10em f-w bb"
          :to="createModalEditionLink(problem.id, $route, fwModalQueryParam)"
        >
          <div class="generic-border__left">
            <Icon class="generic-border__left-blue-icon">{{ icons.fiveWhys }}</Icon>
            <span>{{ $t('message.FIVE_WHYS') }}</span>
          </div>
          <Icon class="generic-border__right-arrow">{{ icons.arrowDownLine }}</Icon>
        </RouterLink>
        <RouterLink
          class="generic-border generic-border--text-btn generic-border--blue-border mt10em f-w bb"
          :to="createModalEditionLink(problem.id, $route, ishikawaModalQueryParam)"
        >
          <div class="generic-border__left">
            <Icon class="generic-border__left-blue-icon">{{ icons.ishikawa }}</Icon>
            <span>{{ $t('message.ISHIKAWA') }}</span>
          </div>
          <Icon class="generic-border__right-arrow">{{ icons.arrowDownLine }}</Icon>
        </RouterLink>
      </div>
    </div>

    <!-- Hidden components -->
    <FileViewer
      v-if="filesHolder && fileViewerVisible"
      ref="fileViewer"
      v-model="filesHolder.files"
      :editable="!disabled"
      :index="fileViewerIndex"
      :pathname="fileViewerPathname"
      @input="fileViewerInput"
      @close="fileViewerVisible = false"
    />
  </div>
</template>

<script>
/**
 * Causes section of the problem sheet
 *
 * @vue-prop {Object} problem - Problem object
 * @vue-prop {Number} [selectedCauseId] - Cause selected to filter actions
 * @vue-prop {Boolean} [disabled] - Whether the problem sheet is editable
 */

import toastr from 'toastr';
// Vuex
import { EDIT_CAUSE, DELETE_CAUSE, ADD_CAUSE } from '@/store/modules/problem/action-types';
// Inport components
import CauseItem from '@/components/cause-item.vue';
import FileViewer from '@/components/file-system/file-viewer.vue';
import Icon from '@/components/icon.vue';
import ProblemTrust from '@/components/problem-sheet/problem-trust.vue';
// Import socket handling utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
// Import utils
import { createModalEditionLink } from '@/utils/navigation';
import { fwModalQueryParam, ishikawaModalQueryParam } from '@/utils/navigation-constants';
import { calert, preventPastingStyles, nestedFlatten } from '@/utils/main';
import * as icons from '@/assets/icons';

export default {

  name: 'ProblemCausesSection',

  components: {
    CauseItem,
    FileViewer,
    Icon,
    ProblemTrust,
  },

  mixins: [mixinSocketHandler],

  props: {
    problem: {
      type: Object,
      required: true,
    },
    selectedCauseId: {
      type: Number,
      default: null,
    },
    disabled: Boolean,
  },

  data() {
    return {
      icons,
      fwModalQueryParam,
      ishikawaModalQueryParam,
      // File viewer
      filesHolder: null,
      editedFiles: [],
      fileViewerVisible: false,
      fileViewerIndex: 0,
      fileViewerPathname: '',
    };
  },

  computed: {
    causesInSheet() {
      return this.allCauses.filter(cause => cause.hideInSheet === 0);
    },
    /** All the causes for this problem in a linear array */
    allCauses() {
      return nestedFlatten(this.problem.wCauses || [], 'wCauses');
    },
    /** Number: Trust index of all causes between 0 and 1. WARNING: This is used in IAAnalyser */
    causesTrust() {
      if (!this.causesInSheet || this.causesInSheet.length === 0) {
        return 0;
      }
      const trustIndex = this.causesInSheet.filter(cause => cause.isVerified).length;
      return trustIndex / this.causesInSheet.length;
    },
    /** Object. Key: Cause ID ; Value: Cause object */
    causesById() {
      return this.allCauses.reduce((all, cause) => ({ ...all, [`${cause.id}`]: cause }), {});
    },
    /** Array: assignable teams for this problem */
    allTeams() {
      return this.$store.getters['problem/teamsForProblem'](this.problem.id);
    },
  },

  methods: {
    createModalEditionLink,
    /** Cause text was edited */
    causeEdited(cause, fields) {
      const params = { id: cause.id, problemId: this.problem.id };
      fields = fields instanceof Array ? fields : [fields];
      fields.forEach((field) => {
        params[field] = cause[field];
      });
      this.$store.dispatch(`problem/${EDIT_CAUSE}`, params)
        .then(() => {
          toastr.remove();
          toastr.success(this.$t('message.CAUSE_SAVED'));
          this.sendSocketUpdate();
        })
        .catch((msg) => {
          if (msg === -2) {
            calert(this.$t('message.PB_NO_ACCESS'), [{ title: this.$t('message.OK') }]);
          } else if (msg === -3) {
            // Looping detected: Cancel silently
          } else {
            calert(this.$t('message.CAUSE_EDITION_ERROR'), [{ title: this.$t('message.OK') }]);
          }
        });
    },
    /** Add a cause with no specific attribute */
    addCause(parent, text) {
      const type = parent === undefined ? 0 : 1;
      const params = { id: this.problem.id, cause: parent ? parent.id : undefined, text, type };
      this.$store.dispatch(`problem/${ADD_CAUSE}`, params)
        .then(() => {
          preventPastingStyles();
          toastr.remove();
          toastr.success(this.$t('message.CAUSE_SAVED'));
          this.sendSocketUpdate();
        })
        .catch((msg) => {
          if (msg === -2) {
            calert(this.$t('message.PB_NO_ACCESS'), [{ title: this.$t('message.OK') }]);
          } else {
            calert(this.$t('message.PB_EDITION_ERROR'), [{ title: this.$t('message.OK') }]);
          }
        });
    },
    deleteCause(cause) {
      this.$store.dispatch(`problem/${DELETE_CAUSE}`, { id: cause.id })
        .then(() => {
          this.sendSocketUpdate();
          toastr.remove();
          toastr.success(this.$t('message.CAUSE_DELETED'));
          if (this.selectedCauseId && this.selectedCauseId === cause.id) {
            this.$emit('selectCause', null);
          }
        })
        .catch((msg) => {
          if (msg === -2) {
            calert(this.$t('message.PB_NO_ACCESS'), [{ title: this.$t('message.OK') }]);
          } else {
            calert(this.$t('message.PB_EDITION_ERROR'), [{ title: this.$t('message.OK') }]);
          }
        });
    },
    editFiles(item, index = 0) {
      if (!this.disabled) {
        this.fileViewerIndex = index;
        this.fileViewerVisible = true;
        item.originalFiles = item.files || [];
        this.filesHolder = item;
        this.fileViewerPathname = this.filesHolder.lWhat !== undefined ? 'problems/' : 'causes/';
      }
    },
    fileViewerInput() {
      const params = ['files'];
      if (this.filesHolder.files.length > 0 && this.filesHolder.originalFiles.length === 0) {
        this.filesHolder.isVerified = 1;
        this.filesHolder.isReal = 1;
        params.push('isVerified');
        params.push('isReal');
      }
      this.causeEdited(this.filesHolder, params);
    },
    /** Select / unselect a cause */
    causeClicked(cause) {
      this.$emit('selectCause', cause);
    },
    /** Send a socket msg to notify teams that this problem sheet can be refreshed */
    sendSocketUpdate() {
      this.sendSocketMessage({
        msg: SocketMessages.CC_MSGS.PROBLEM_SHEET,
        id: this.problem.id,
        socketCompany: 1,
      });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.problem-causes-section {
  &__causes {
    margin-bottom: 3.5rem;

    .cause-item, .generic-border--blue-border.generic-border--text-btn {
      margin-top: 0.8rem;
    }
  }
}
</style>
